import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import VirtualizedCardGrid from "../layouts/VirtualizedCardGrid";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import TeacherSubjectCard from "../components/teacherSubjects/TeacherSubjectCard";
import CreateTeacherSubjectModal from "../components/teacherSubjects/CreateTeacherSubjectModal";
import UpdateTeacherSubjectModal from "../components/teacherSubjects/UpdateTeacherSubjectModal";

import { usePermissions } from "../hooks/usePermissions";
import useDebounce from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTeacherSubjects,
  deleteTeacherSubject,
  resetTeacherSubjectServerStatus,
} from "../redux/actions/teacherSubjectActions";
import { TeacherSubjectResponseDto } from "../shared/dtos/teacherSubject/TeacherSubjectResponseDto";

const TeacherSubjectsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: teacherSubjects,
    loadingFetchAll: loadingTeacherSubjects,
    errorFetchAll: errorLoadingTeacherSubjects,
    serverStatus,
    loadingCreate: loadingTeacherSubjectCreate,
    errorCreate: errorCreatingTeacherSubject,
    loadingUpdate: loadingTeacherSubjectUpdate,
    errorUpdate: errorUpdatingTeacherSubject,
    loadingDelete: loadingTeacherSubjectDelete,
  } = useAppSelector((state) => state.teacherSubject);

  // --- LOCAL FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 150);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  // --- MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTeacherSubject, setSelectedTeacherSubject] =
    useState<TeacherSubjectResponseDto | null>(null);

  // --- PERMISSIONS ---
  const canCreate = hasPermission("teacher:create");
  const canUpdate = hasPermission("teacher:update");
  const canDelete = hasPermission("teacher:delete");

  // Fetch full list (receives branchId if super admin selects one)
  const fetchTeacherSubjectsList = useCallback((): void => {
    dispatch(
      getTeacherSubjects(
        selectedBranchId ? { branchId: selectedBranchId } : undefined,
      ),
    );
  }, [dispatch, selectedBranchId]);

  useEffect(() => {
    fetchTeacherSubjectsList();
  }, [fetchTeacherSubjectsList]);

  // --- FRONTEND SEARCH & FILTER ---
  const filteredTeacherSubjects = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();

    return teacherSubjects.filter((ts) => {
      if (!query) return true;

      const matchesTeacher = ts.teacherName?.toLowerCase().includes(query);
      const matchesSubject = ts.subjectName?.toLowerCase().includes(query);
      const matchesAcademicYear = ts.academicYearName
        ?.toLowerCase()
        .includes(query);

      return Boolean(matchesTeacher || matchesSubject || matchesAcademicYear);
    });
  }, [debouncedSearch, teacherSubjects]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingTeacherSubjectCreate) setIsCreateModalOpen(false);
  }, [loadingTeacherSubjectCreate]);

  const handleOpenUpdate = (item: TeacherSubjectResponseDto): void => {
    setSelectedTeacherSubject(item);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingTeacherSubjectUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTeacherSubject(null);
    }
  }, [loadingTeacherSubjectUpdate]);

  const handleOpenDelete = (item: TeacherSubjectResponseDto): void => {
    setSelectedTeacherSubject(item);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback((): void => {
    if (!loadingTeacherSubjectDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTeacherSubject(null);
    }
  }, [loadingTeacherSubjectDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedTeacherSubject) {
      dispatch(deleteTeacherSubject(selectedTeacherSubject.id));
    }
  };

  // Auto-close modals on server status success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetTeacherSubjectServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Teacher Subjects"
      subtitle="Manage teacher subject allocations across academic years."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Assign Subject
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by teacher, subject, or academic year..."
      >
        {/* Rendered only for SUPER_ADMIN */}
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => setSelectedBranchId(bId)}
        />
      </PageToolbar>

      <VirtualizedCardGrid
        data={filteredTeacherSubjects}
        loading={loadingTeacherSubjects}
        error={errorLoadingTeacherSubjects}
        emptyMessage="No teacher-subject allocations match your search criteria"
        onRetry={fetchTeacherSubjectsList}
        renderCard={(ts) => (
          <TeacherSubjectCard
            key={ts.id}
            teacherSubject={ts}
            onEdit={canUpdate ? () => handleOpenUpdate(ts) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(ts) : undefined}
          />
        )}
      />

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateTeacherSubjectModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingTeacherSubjectCreate}
          error={errorCreatingTeacherSubject}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedTeacherSubject && (
        <UpdateTeacherSubjectModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          teacherSubject={selectedTeacherSubject}
          loading={loadingTeacherSubjectUpdate}
          error={errorUpdatingTeacherSubject}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedTeacherSubject && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Allocation"
          description={`Are you sure you want to remove the allocation for "${selectedTeacherSubject.teacherName}" teaching "${selectedTeacherSubject.subjectName}"?`}
          loading={loadingTeacherSubjectDelete}
        />
      )}
    </PageContainer>
  );
};

export default TeacherSubjectsPage;
